import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Batch-resolves other members' PUBLIC LR Hub identity through the
 * `hub_display_identities` RPC. The RPC returns only display-safe fields:
 * display_name, handle, role (omitted when the member hid it), the admin-assigned
 * special badge and an avatar path that is
 * non-null ONLY when the photo was approved by moderation.
 */

export interface HubDisplayIdentity {
  user_id: string;
  display_name: string;
  handle: string;
  role: string | null;
  avatar_path: string | null;
  /** Special Hub badge (admin-assigned only): founder | founding_member | ... */
  hub_badge: string | null;
}

/** Prefix for every identity cache entry — invalidate after a settings change. */
export const HUB_IDENTITIES_KEY = ["hub-display-identities"] as const;

export function useHubIdentities(userIds: (string | null | undefined)[]) {
  const ids = Array.from(new Set(userIds.filter((v): v is string => !!v))).sort();

  const query = useQuery({
    queryKey: [...HUB_IDENTITIES_KEY, ids.join(",")],
    enabled: ids.length > 0,
    // Short window: display name / @handle / "show my role" / avatar changes
    // must surface on the feed almost immediately (read LIVE, never baked
    // onto the post row).
    staleTime: 30_000,

    queryFn: async (): Promise<HubDisplayIdentity[]> => {
      const { data, error } = await supabase.rpc("hub_display_identities", { _user_ids: ids });
      if (error) throw error;
      return (data ?? []) as HubDisplayIdentity[];
    },
  });

  const map = new Map<string, HubDisplayIdentity>();
  for (const row of query.data ?? []) map.set(row.user_id, row);

  return { map, isLoading: query.isLoading };
}

export default useHubIdentities;
